/**
 * El ultimo recurso cuando algo revienta durante el render.
 *
 * Sin esto, cualquier excepcion — un `lazy()` que no pudo bajar su trozo con
 * la red intermitente, un componente de tercero que tira, una respuesta
 * inesperada — desmonta el arbol entero y deja la pantalla **en blanco, sin
 * una palabra**. Para quien lo sufre no hay diferencia entre eso y "la
 * aplicacion no anda", y para quien tiene que arreglarlo tampoco: no queda
 * nada que mirar salvo la consola, que nadie abre.
 *
 * Lo mas util que puede hacer entonces es decir que paso y ofrecer volver a
 * cargar. El mensaje del error se muestra plegado: son errores del navegador,
 * en codigo que de todas formas es publico, y es la diferencia entre un
 * reporte accionable y "no funciona".
 */

import { Component } from "react";
import type { ErrorInfo, ReactNode } from "react";

interface Props {
  readonly children: ReactNode;
}

interface State {
  readonly error: Error | null;
}

export class LimiteDeError extends Component<Props, State> {
  override state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  override componentDidCatch(error: Error, info: ErrorInfo): void {
    // Que quede en la consola con el arbol de componentes: es lo unico que
    // permite ubicar donde reviento cuando alguien reporta la pantalla.
    console.error("TurnPro: error no controlado en el render", error, info.componentStack);
  }

  override render(): ReactNode {
    const { error } = this.state;
    if (!error) return this.props.children;

    return (
      <main className="mx-auto flex min-h-screen max-w-lg flex-col items-center justify-center gap-4 px-4 text-center">
        <h1 className="titulo text-xl font-semibold">No pudimos mostrar esta pantalla</h1>
        <p className="text-muted">
          Algo fallo al cargarla. Volver a cargar la pagina suele alcanzar; si sigue pasando,
          avisanos con el detalle de abajo.
        </p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
        >
          Volver a cargar
        </button>
        <details className="w-full text-left text-sm text-muted">
          <summary className="cursor-pointer">Detalle tecnico</summary>
          <pre className="mt-2 overflow-x-auto whitespace-pre-wrap break-words rounded-lg bg-[var(--surface-muted)] p-3 text-xs">
            {error.message}
          </pre>
        </details>
      </main>
    );
  }
}
